import type { CreateUserPayload } from '../types';
import {
  defaultWorkspaceAccessForRole,
  permissionsForWorkspaceAccess,
  type WorkspaceAccessKey,
} from '@/shared/permissions/workspaceAccess';

export type CreateUserRole = 'admin' | 'department_admin' | 'manager' | 'team_lead' | 'bde' | 'developer' | 'employee' | 'field_executive' | 'sales_coordinator';
export interface CreateUserFormState {
  username: string;
  displayName: string;
  password: string;
  shiftType: 'domestic' | 'international';
  role: CreateUserRole;
  /**
   * Job title, e.g. "Full Stack Developer" — set from Designation master
   * data. Independent of `role`: selecting a designation derives `role` from
   * its `baseRole`, but the designation label itself is what gets stored on
   * the user (as `position`), not reused as the role value (2026-08-16 —
   * these were previously the same field, which broke role-based permission
   * defaults whenever real designation master data existed).
   */
  designation: string;
  department: string;
  workspaceAccess: WorkspaceAccessKey[];
}

export const CREATE_USER_DEPARTMENTS = ['sales', 'hr', 'it', 'digital_marketing', 'accounts'] as const;

export interface CreateUserRoleOption {
  value: CreateUserRole;
  label: string;
  description: string;
}

const EMPLOYEE: CreateUserRoleOption = {
  value: 'employee', label: 'Employee', description: 'Standard department member access',
};
const TEAM_LEAD: CreateUserRoleOption = {
  value: 'team_lead', label: 'Team Lead', description: 'Coordinates a department team',
};
const MANAGER: CreateUserRoleOption = {
  value: 'manager', label: 'Department Manager', description: 'Manages work and department reporting',
};
const ADMIN: CreateUserRoleOption = {
  value: 'admin', label: 'Department Administrator', description: 'Administers users and department operations',
};

// Field Executive (2026-10-06): customer visits with check-in / out, leads,
// enquiries, contacts, follow-ups, sample requests and expense claims — no
// approvals, deletes, admin or accounts (authority/permission-catalog.js).
const FIELD_EXECUTIVE: CreateUserRoleOption = {
  value: 'field_executive', label: 'Field Executive', description: 'Customer visits (check in / out), leads, follow-ups, samples, expense claims',
};

const SALES_COORDINATOR: CreateUserRoleOption = {
  value: 'sales_coordinator', label: 'Sales Coordinator', description: 'Prepares and finalizes quotations and invoices on behalf of reps, and uploads dispatch documents',
};

export const DEPARTMENT_ROLE_OPTIONS: Record<string, CreateUserRoleOption[]> = {
  field_executive: [FIELD_EXECUTIVE, EMPLOYEE, TEAM_LEAD, MANAGER],
  sales: [
    EMPLOYEE,
    { value: 'bde', label: 'Business Development Executive', description: 'Leads, outreach, calls and sales follow-ups' },
    SALES_COORDINATOR,
    FIELD_EXECUTIVE,
    TEAM_LEAD,
    MANAGER,
    ADMIN,
  ],
  hr: [EMPLOYEE, TEAM_LEAD, MANAGER, ADMIN],
  it: [
    EMPLOYEE,
    { value: 'developer', label: 'Developer', description: 'IT delivery, projects and technical tasks' },
    TEAM_LEAD,
    MANAGER,
    ADMIN,
  ],
  digital_marketing: [EMPLOYEE, TEAM_LEAD, MANAGER, ADMIN],
  accounts: [EMPLOYEE, MANAGER, ADMIN],
};

/** Every role the platform recognises, for the "change this employee's authority" dropdown — unlike DEPARTMENT_ROLE_OPTIONS, not scoped to what a department may pick at creation time. */
export const AUTHORITY_TIER_OPTIONS: CreateUserRoleOption[] = [
  { value: 'employee', label: 'Employee', description: 'Standard user with only the modules and actions you assign.' },
  { value: 'bde', label: 'BDE', description: 'Sales execution role; exact actions remain configurable.' },
  SALES_COORDINATOR,
  { value: 'field_executive', label: 'Field Executive', description: 'Customer visits, leads, follow-ups, samples and expense claims; no approvals or deletes.' },
  { value: 'team_lead', label: 'Team Lead', description: 'Team coordination role with configurable actions.' },
  { value: 'manager', label: 'Manager', description: 'Department management role with configurable actions.' },
  { value: 'developer', label: 'Developer', description: 'Technical user; exact access remains configurable.' },
  { value: 'department_admin', label: 'Department Administrator', description: 'Administers users and work inside one department.' },
  { value: 'admin', label: 'Administrator', description: 'Organization administrator below the owner account.' },
];

export function rolesForDepartment(department: string): CreateUserRoleOption[] {
  const norm = (department || '').toLowerCase().replace(/\s+/g, '_');
  if (DEPARTMENT_ROLE_OPTIONS[norm]) return DEPARTMENT_ROLE_OPTIONS[norm];
  if (DEPARTMENT_ROLE_OPTIONS[department]) return DEPARTMENT_ROLE_OPTIONS[department];
  // Dynamic or custom department from master data: support standard organizational roles
  return [
    EMPLOYEE,
    { value: 'bde', label: 'Business Development Executive', description: 'Leads, outreach, and sales follow-ups' },
    SALES_COORDINATOR,
    { value: 'developer', label: 'Technical / Developer', description: 'Technical operations and projects' },
    FIELD_EXECUTIVE,
    TEAM_LEAD,
    MANAGER,
    ADMIN,
  ];
}

export function isRoleAllowedForDepartment(department: string, role: string): role is CreateUserRole {
  return rolesForDepartment(department).some((option) => option.value === role);
}

export function initialCreateUserForm(
  department: string,
  options: { requireDepartmentSelection?: boolean } = {},
): CreateUserFormState {
  const selectedDepartment = options.requireDepartmentSelection ? '' : (department || 'sales');
  return {
    username: '',
    displayName: '',
    password: '',
    shiftType: 'domestic',
    role: 'employee',
    designation: '',
    department: selectedDepartment,
    workspaceAccess: selectedDepartment ? defaultWorkspaceAccessForRole(selectedDepartment, 'employee') : [],
  };
}

export function buildCreateUserPayload(
  form: CreateUserFormState,
  requester: { department: string; isSuperAdmin: boolean },
): CreateUserPayload | null {
  const username = form.username.trim().toLowerCase();
  const displayName = form.displayName.trim();
  if (!username || !displayName || form.password.length < 6) return null;
  // The role is sent as chosen; the server is the authority on whether this
  // requester may create it (administrator-tier roles stay owner-only).
  const targetRole = form.role || 'employee';
  const rawDept = requester.isSuperAdmin ? (form.department || 'sales') : (requester.department || 'sales');
  const targetDept = rawDept.trim().toLowerCase().replace(/\s+/g, '_');

  // Whatever the superadmin left checked in the Module Access step IS the
  // grant — full stop. Department/role defaults only seed the initial
  // checkbox state (see initialCreateUserForm / setDepartment below); they
  // must never be re-unioned back in here, or unchecking a default module
  // would silently do nothing and the superadmin would have no way to deny
  // it to a new hire.
  const allModules = [...new Set(form.workspaceAccess || [])];

  // A department administrator's module choices are sent too. They used to be
  // dropped here AND ignored by the server, so the picker's state was pure
  // theatre and every account inherited its department defaults instead. The
  // server now accepts a grant up to what the requester holds and refuses
  // anything beyond it out loud.
  return {
    username,
    displayName,
    password: form.password,
    department: targetDept,
    shiftType: form.shiftType || 'domestic',
    role: targetRole,
    ...(form.designation ? { designation: form.designation } : {}),
    permissions: permissionsForWorkspaceAccess(allModules),
  };
}
